import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  MapPin, Loader2, Compass, Users, Clock, ChevronRight,
  AlertTriangle, BatteryCharging, Sun, Calendar, HardHat, CheckCircle2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All install types' },
  { value: 'battery_only', label: 'Battery only' },
  { value: 'solar', label: 'Solar only' },
  { value: 'solar_battery', label: 'Solar + Battery' },
];

export const SmartSchedule = () => {
  const [installType, setInstallType] = useState('all');
  const [radius, setRadius] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Cluster booking dialog
  const [bookCluster, setBookCluster] = useState(null);  // selected cluster object
  const [people, setPeople] = useState([]);
  const [bookEngineers, setBookEngineers] = useState([]);
  const [bookStartDate, setBookStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookSkipWeekends, setBookSkipWeekends] = useState(true);
  const [bookIncludeClashes, setBookIncludeClashes] = useState(false);
  const [booking, setBooking] = useState(false);
  const [bookResult, setBookResult] = useState(null);

  const loadPeople = async () => {
    try {
      const r = await api.get('/installation-people');
      setPeople(r.data || []);
    } catch {
      setPeople([]);
    }
  };

  useEffect(() => { loadPeople(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/smart-schedule/clusters', {
        params: { install_type: installType, cluster_radius: radius },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [installType, radius]);

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="smart-schedule-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
            <Compass className="inline w-7 h-7 mr-2 text-blue-600" />
            Smart Schedule
          </h1>
          <p className="text-slate-500 mt-1">
            Pending installs grouped by real geographic distance (postcodes.io). Schedule clusters
            on consecutive days to cut driving time and fuel.
          </p>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Install type</Label>
                <Select value={installType} onValueChange={setInstallType}>
                  <SelectTrigger data-testid="type-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cluster radius (miles)</Label>
                <Input
                  type="number"
                  min="3" max="40" step="1"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value) || 12)}
                  data-testid="radius-input"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={load} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Compass className="w-4 h-4 mr-2" />}
                  Refresh clusters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading && !data ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin inline text-blue-500" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Pending installs</div><div className="text-2xl font-bold text-slate-900">{data.total_pending}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Geocoded</div><div className="text-2xl font-bold text-emerald-600">{data.geocoded_count}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-slate-500">Clusters found</div><div className="text-2xl font-bold text-blue-600">{data.clusters?.length || 0}</div></CardContent></Card>
            </div>

            {data.clusters?.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-400">
                <Compass className="w-10 h-10 mx-auto mb-2" />
                <p>No clusters yet. Check that customers in pending status have valid UK postcodes.</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {data.clusters.map((c, idx) => (
                  <Card key={`${c.centre_postcode}-${idx}`} data-testid={`cluster-${idx}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          {c.centre_postcode}
                          <span className="text-slate-400 font-normal text-sm">· centred on {c.centre_name}</span>
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700"><Users className="w-3 h-3 mr-1" />{c.customer_count} {c.customer_count === 1 ? 'job' : 'jobs'}</Badge>
                          <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />{c.total_install_days} days</Badge>
                          <Badge variant="outline">{c.radius_miles} mi spread</Badge>
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                            onClick={() => {
                              setBookCluster(c);
                              setBookEngineers([]);
                              setBookStartDate(new Date().toISOString().slice(0, 10));
                              setBookSkipWeekends(true);
                              setBookIncludeClashes(false);
                              setBookResult(null);
                            }}
                            data-testid={`schedule-cluster-btn-${idx}`}
                          >
                            <Calendar className="w-3 h-3 mr-1" /> Schedule cluster
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {c.customers.map((cust) => {
                          const isCentre = !cust.distance_from_centre;
                          const TypeIcon = (cust.installation_type || '').toLowerCase().includes('battery') ? BatteryCharging : Sun;
                          return (
                            <Link
                              key={cust.id}
                              to={`/customers/${cust.id}`}
                              className="flex items-center gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50 hover:border-blue-300 transition"
                              data-testid={`cluster-${idx}-cust-${cust.id}`}
                            >
                              <TypeIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-slate-900 truncate">{cust.first_name} {cust.last_name}</div>
                                <div className="text-[11px] text-slate-500 truncate">{cust.address}{cust.city ? `, ${cust.city}` : ''} {cust.postcode}</div>
                              </div>
                              <Badge variant="outline" className="text-[10px] flex-shrink-0">
                                {isCentre ? 'centre' : `${cust.distance_from_centre} mi`}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            </Link>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {data.ungeocoded?.length > 0 && (
              <Card className="border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" />
                    {data.ungeocoded.length} customers couldn't be geocoded
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-500 mb-2">Their postcodes are invalid, foreign, or temporarily unavailable. Open each record and double-check the postcode.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                    {data.ungeocoded.map((c) => (
                      <Link key={c.id} to={`/customers/${c.id}`} className="text-xs text-blue-600 hover:underline truncate">
                        {c.first_name} {c.last_name} · {c.postcode || '(no postcode)'}
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}

        {/* Schedule Cluster Dialog */}
        <Dialog open={!!bookCluster} onOpenChange={(open) => { if (!open && !booking) { setBookCluster(null); setBookResult(null); } }}>
          <DialogContent className="max-w-2xl" data-testid="schedule-cluster-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Schedule Cluster — {bookCluster?.centre_postcode}
              </DialogTitle>
              <DialogDescription>
                Books consecutive installation dates for all {bookCluster?.customer_count || 0} customers
                in this cluster. Each customer's <strong>installation_days</strong> is honoured back-to-back.
              </DialogDescription>
            </DialogHeader>

            {!bookResult ? (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>First-job start date</Label>
                    <Input type="date" value={bookStartDate} onChange={(e) => setBookStartDate(e.target.value)} data-testid="cluster-start-date" />
                  </div>
                  <div className="flex items-end justify-between gap-3">
                    <div className="flex-1">
                      <Label htmlFor="cluster-skip">Skip weekends</Label>
                    </div>
                    <Switch id="cluster-skip" checked={bookSkipWeekends} onCheckedChange={setBookSkipWeekends} data-testid="cluster-skip-weekends" />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="flex-1">
                    <Label htmlFor="cluster-clash" className="flex items-center gap-2 cursor-pointer">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      Allow clashes (book even if dates already taken)
                    </Label>
                  </div>
                  <Switch id="cluster-clash" checked={bookIncludeClashes} onCheckedChange={setBookIncludeClashes} data-testid="cluster-clashes" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <HardHat className="w-4 h-4 text-blue-600" />
                    Assign engineers / staff (multiple allowed)
                  </Label>
                  {people.length === 0 ? (
                    <p className="text-xs text-slate-400">No engineers / staff available.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 p-3 border border-slate-200 rounded-md bg-slate-50 max-h-[180px] overflow-y-auto">
                      {people.map((p) => {
                        const jobs = p.job_count_30d || 0;
                        const loadColor = jobs === 0 ? 'text-slate-400' : jobs < 3 ? 'text-emerald-600' : jobs < 6 ? 'text-amber-600' : 'text-rose-600';
                        return (
                          <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-white px-2 py-1 rounded" data-testid={`cluster-person-${p.id}`}>
                            <input
                              type="checkbox"
                              checked={bookEngineers.includes(p.id)}
                              onChange={() => setBookEngineers((prev) => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700 truncate flex-1">{p.name}</span>
                            <span className={`text-[10px] font-medium ${loadColor}`} title={`${jobs} job${jobs === 1 ? '' : 's'} in next 30 days`}>
                              {jobs}j
                            </span>
                            <Badge variant="outline" className="text-[9px] px-1">
                              {p.type === 'engineer' ? 'Eng' : (p.type === 'admin' ? 'Admin' : 'Staff')}
                            </Badge>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {bookEngineers.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1">{bookEngineers.length} {bookEngineers.length === 1 ? 'person' : 'people'} will be assigned to every job in this cluster.</p>
                  )}
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
                  <div className="font-semibold mb-1 text-slate-700">Cluster summary</div>
                  <div className="text-slate-600">
                    {bookCluster?.customer_count} jobs · {bookCluster?.total_install_days} install days total · {bookCluster?.radius_miles} mi spread
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <div className="text-sm">
                    <div className="font-semibold text-emerald-900">
                      {bookResult.total_booked} of {(bookResult.total_booked + bookResult.total_skipped)} jobs booked
                    </div>
                    {bookResult.total_skipped > 0 && (
                      <div className="text-amber-700 text-xs">{bookResult.total_skipped} skipped (no available span)</div>
                    )}
                  </div>
                </div>
                {bookResult.booked.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Booked</div>
                    {bookResult.booked.map((b) => (
                      <div key={b.customer_id} className="flex items-center justify-between p-2 border border-emerald-200 bg-emerald-50/50 rounded text-sm" data-testid={`booked-${b.customer_id}`}>
                        <span className="font-medium">{b.name}</span>
                        <span className="text-xs text-slate-600">{b.scheduled_date}{b.end_date !== b.scheduled_date ? ` → ${b.end_date}` : ''} ({b.installation_days}d)</span>
                      </div>
                    ))}
                  </div>
                )}
                {bookResult.skipped.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs uppercase tracking-wider font-semibold text-amber-700">Skipped</div>
                    {bookResult.skipped.map((s) => (
                      <div key={s.customer_id} className="flex items-center justify-between p-2 border border-amber-200 bg-amber-50/50 rounded text-sm">
                        <span className="font-medium">{s.name}</span>
                        <span className="text-xs text-amber-700">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              {!bookResult ? (
                <>
                  <Button variant="outline" onClick={() => setBookCluster(null)} disabled={booking}>Cancel</Button>
                  <Button
                    onClick={async () => {
                      if (!bookCluster) return;
                      setBooking(true);
                      try {
                        const r = await api.post('/smart-schedule/cluster/book', {
                          customer_ids: bookCluster.customers.map(c => c.id),
                          engineer_ids: bookEngineers,
                          start_date: bookStartDate,
                          skip_weekends: bookSkipWeekends,
                          include_clashes: bookIncludeClashes,
                        });
                        setBookResult(r.data);
                        toast.success(`${r.data.total_booked} jobs booked`);
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Cluster booking failed');
                      } finally {
                        setBooking(false);
                      }
                    }}
                    disabled={booking}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="confirm-cluster-book"
                  >
                    {booking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                    Book {bookCluster?.customer_count} jobs
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => { setBookCluster(null); setBookResult(null); load(); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="cluster-book-done"
                >
                  Done
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SmartSchedule;
